import type {
  WorkflowNode,
  WorkflowScene,
  WorkflowSelectorConfig,
  WorkflowTimelineItem,
} from '@cms/models';
import {
  normalizeProjectImageAspectRatio,
  normalizeProjectImageDownloadQuality,
  normalizeProjectImageModel,
  normalizeProjectOutputCount,
  normalizeProjectVideoAspectRatio,
  normalizeProjectVideoDownloadQuality,
  normalizeProjectVideoModel,
} from '@cms/project-generation-options';
import {
  extractWorkflowPrompt,
  formatWorkflowPromptForDisplay,
  normalizeWorkflowPrompt,
  workflowPromptFromImportCell,
} from '@cms/utils';
import * as XLSX from 'xlsx';

/**
 * API returns TypeORM-shaped JSON (`timelineNodes`); Zod/docs use `timeline_nodes`.
 * Accept both so view/edit forms populate after GET /projects/:id.
 */
export function timelineNodesFromApiPayload(
  w: { timeline_nodes?: WorkflowTimelineItem[]; timelineNodes?: WorkflowTimelineItem[] } | null | undefined,
): WorkflowTimelineItem[] {
  const n = w?.timeline_nodes ?? w?.timelineNodes;
  return Array.isArray(n) ? n : [];
}

/** API may expose `tenantId` (TypeORM) or `tenant_id` (Zod). */
export function projectTenantIdFromApi(
  w: { tenant_id?: string | null; tenantId?: string | null } | null | undefined,
): string | null {
  const t = w?.tenant_id ?? w?.tenantId;
  return typeof t === 'string' && t.length > 0 ? t : null;
}

/** Prompt templates from GET workflow (plain text or JSON; snake_case or camelCase). */
export function projectPromptTemplatesFromApi(
  w: {
    image_prompt_template?: unknown;
    imagePromptTemplate?: unknown;
    video_prompt_template?: unknown;
    videoPromptTemplate?: unknown;
  } | null
    | undefined,
): { image: string; video: string } {
  if (w == null) return { image: '', video: '' };
  const imageStored = normalizeWorkflowPrompt(
    extractWorkflowPrompt(w.image_prompt_template ?? w.imagePromptTemplate),
  );
  const videoStored = normalizeWorkflowPrompt(
    extractWorkflowPrompt(w.video_prompt_template ?? w.videoPromptTemplate),
  );
  return {
    image: imageStored ? formatWorkflowPromptForDisplay(imageStored) : '',
    video: videoStored ? formatWorkflowPromptForDisplay(videoStored) : '',
  };
}

export function projectGenerationSettingsFromApi(
  w: Record<string, unknown> | null | undefined,
): {
  imageModel: string;
  imageAspectRatio: string;
  imageOutputCount: number;
  imageDownloadQuality: string;
  videoModel: string;
  videoAspectRatio: string;
  videoOutputCount: number;
  videoDownloadQuality: string;
} {
  const imageModelRaw = w?.image_model ?? w?.imageModel;
  const imageAspectRatioRaw = w?.image_aspect_ratio ?? w?.imageAspectRatio;
  const imageOutputRaw = w?.image_output_count ?? w?.imageOutputCount;
  const imageDownloadQualityRaw =
    w?.image_download_quality ?? w?.imageDownloadQuality;
  const videoModelRaw = w?.video_model ?? w?.videoModel;
  const videoAspectRatioRaw = w?.video_aspect_ratio ?? w?.videoAspectRatio;
  const videoOutputRaw = w?.video_output_count ?? w?.videoOutputCount;
  const videoDownloadQualityRaw =
    w?.video_download_quality ?? w?.videoDownloadQuality;

  return {
    imageModel: normalizeProjectImageModel(imageModelRaw),
    imageAspectRatio: normalizeProjectImageAspectRatio(imageAspectRatioRaw),
    imageOutputCount: normalizeProjectOutputCount(imageOutputRaw),
    imageDownloadQuality: normalizeProjectImageDownloadQuality(imageDownloadQualityRaw),
    videoModel: normalizeProjectVideoModel(videoModelRaw),
    videoAspectRatio: normalizeProjectVideoAspectRatio(videoAspectRatioRaw),
    videoOutputCount: normalizeProjectOutputCount(videoOutputRaw),
    videoDownloadQuality: normalizeProjectVideoDownloadQuality(videoDownloadQualityRaw),
  };
}

/** API may expose `selectorConfig` (TypeORM) or `selector_config` (snake_case). */
export function projectSelectorConfigFromApi(
  w:
    | { selector_config?: WorkflowSelectorConfig; selectorConfig?: WorkflowSelectorConfig }
    | null
    | undefined,
): WorkflowSelectorConfig {
  const raw = w?.selector_config ?? w?.selectorConfig;
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return raw;
}

export interface WorkflowSelectorRow {
  rowId: string;
  key: string;
  css: string;
  xpath: string;
}

function newSelectorRow(): WorkflowSelectorRow {
  return { rowId: crypto.randomUUID(), key: '', css: '', xpath: '' };
}

export function selectorConfigToRows(config: WorkflowSelectorConfig): WorkflowSelectorRow[] {
  const entries = Object.entries(config);
  if (entries.length === 0) return [newSelectorRow()];
  return entries.map(([k, v]) => {
    if (typeof v === 'string') {
      return { rowId: crypto.randomUUID(), key: k, css: v, xpath: '' };
    }
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const o = v as { css?: unknown; xpath?: unknown };
      const css = typeof o.css === 'string' ? o.css : '';
      const xpath = typeof o.xpath === 'string' ? o.xpath : '';
      return { rowId: crypto.randomUUID(), key: k, css, xpath };
    }
    return { rowId: crypto.randomUUID(), key: k, css: '', xpath: '' };
  });
}

export function rowsToSelectorConfig(rows: WorkflowSelectorRow[]): WorkflowSelectorConfig {
  const out: WorkflowSelectorConfig = {};
  for (const row of rows) {
    const k = row.key.trim();
    if (!k) continue;
    const css = row.css.trim();
    const xp = row.xpath.trim();
    if (!css && !xp) continue;
    if (css && !xp) {
      out[k] = css;
    } else {
      out[k] = {
        ...(css ? { css } : {}),
        ...(xp ? { xpath: xp } : {}),
      };
    }
  }
  return out;
}

export function addEmptySelectorRow(rows: WorkflowSelectorRow[]): WorkflowSelectorRow[] {
  return [...rows, newSelectorRow()];
}

export function removeSelectorRowAt(rows: WorkflowSelectorRow[], index: number): WorkflowSelectorRow[] {
  if (rows.length <= 1) return rows;
  return rows.filter((_, i) => i !== index);
}

export interface ProjectGridRow {
  /** Stable key for list reconciliation (not persisted). */
  rowId: string;
  /** Logical scene id shared by TEXT_TO_IMAGE + IMAGE_TO_VIDEO pair. */
  sceneId: number;
  /** Human-readable scene label (from import sheet column "Scene"). */
  sceneName: string;
  /** Text-to-image prompt for this row */
  text2ImagePrompt: string;
  /** Image-to-video prompt for this row */
  image2VideoPrompt: string;
}

export type WorkflowGridRow = ProjectGridRow;

function promptForGridDisplay(value: unknown): string {
  const stored = normalizeWorkflowPrompt(extractWorkflowPrompt(value));
  return stored ? formatWorkflowPromptForDisplay(stored) : '';
}

function newId(): string {
  return crypto.randomUUID();
}

/**
 * Maps stored timeline nodes (ordered T2I, I2V, T2I, I2V, …) to grid rows.
 * Expects each pair to be TEXT_TO_IMAGE then IMAGE_TO_VIDEO; falls back to reading prompts by type when order differs.
 */
export function timelineNodesToGridRows(nodes: WorkflowTimelineItem[]): ProjectGridRow[] {
  if (!nodes.length) {
    return [{ rowId: newId(), sceneId: 1, sceneName: '', text2ImagePrompt: '', image2VideoPrompt: '' }];
  }
  const rowsByScene = new Map<number, ProjectGridRow>();
  let fallbackSceneId = 1;
  for (const item of nodes) {
    if (item && typeof item === 'object' && 'text2image' in item && 'image2video' in item) {
      const scene = item as WorkflowScene;
      const sceneId =
        Number.isInteger(scene.scene_id) && Number(scene.scene_id) >= 0 ? Number(scene.scene_id) : fallbackSceneId++;
      const text2ImagePrompt = promptForGridDisplay(scene.text2image);
      const image2VideoPrompt = promptForGridDisplay(scene.image2video);
      const sceneName = typeof scene.scene_name === 'string' ? scene.scene_name : '';
      rowsByScene.set(sceneId, {
        rowId: newId(),
        sceneId,
        sceneName,
        text2ImagePrompt,
        image2VideoPrompt,
      });
      continue;
    }
    const node = item as WorkflowNode;
    const rawSceneId = (node as { scene_id?: unknown }).scene_id;
    const sceneId = Number.isInteger(rawSceneId) && Number(rawSceneId) >= 0 ? Number(rawSceneId) : fallbackSceneId++;
    const row =
      rowsByScene.get(sceneId) ??
      { rowId: newId(), sceneId, sceneName: '', text2ImagePrompt: '', image2VideoPrompt: '' };
    if (node.type === 'TEXT_TO_IMAGE') row.text2ImagePrompt = promptForGridDisplay(node.prompt);
    if (node.type === 'IMAGE_TO_VIDEO') row.image2VideoPrompt = promptForGridDisplay(node.prompt);
    rowsByScene.set(sceneId, row);
  }
  const rows: ProjectGridRow[] = [...rowsByScene.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, row]) => row);
  return rows.length
    ? rows
    : [{ rowId: newId(), sceneId: 1, sceneName: '', text2ImagePrompt: '', image2VideoPrompt: '' }];
}

/**
 * Builds timeline_nodes: for each non-empty row, emits TEXT_TO_IMAGE then IMAGE_TO_VIDEO with new UUIDs.
 * @throws Error if a row is partially filled (only one column)
 */
export function gridRowsToTimelineNodes(rows: ProjectGridRow[]): WorkflowScene[] {
  const out: WorkflowScene[] = [];
  for (const [index, row] of rows.entries()) {
    const t2i = normalizeWorkflowPrompt(row.text2ImagePrompt);
    const i2v = normalizeWorkflowPrompt(row.image2VideoPrompt);
    if (!t2i && !i2v) continue;
    if (!t2i || !i2v) {
      throw new Error(
        'Each row must have both Text2Image and Image2Video prompts, or leave both empty.',
      );
    }
    const sceneId =
      Number.isInteger(row.sceneId) && row.sceneId >= 0 ? row.sceneId : index + 1;
    const sceneName = row.sceneName.trim();
    out.push({
      id: newId(),
      scene_id: sceneId,
      ...(sceneName ? { scene_name: sceneName } : {}),
      text2image: t2i,
      image2video: i2v,
    });
  }
  return out;
}

function normalizeImportedRows(rows: ProjectGridRow[]): ProjectGridRow[] {
  return rows
    .filter(
      row =>
        row.text2ImagePrompt.trim().length > 0 ||
        row.image2VideoPrompt.trim().length > 0,
    )
    .map((row, index) => ({
      rowId: newId(),
      sceneId:
        Number.isInteger(row.sceneId) && row.sceneId >= 0 ? row.sceneId : index + 1,
      sceneName: row.sceneName.trim(),
      text2ImagePrompt: normalizeWorkflowPrompt(row.text2ImagePrompt),
      image2VideoPrompt: normalizeWorkflowPrompt(row.image2VideoPrompt),
    }));
}

function parseRowsFromJsonObject(obj: unknown): ProjectGridRow[] {
  if (!obj || typeof obj !== 'object') return [];
  const src = obj as {
    timeline_nodes?: WorkflowTimelineItem[];
    timelineNodes?: WorkflowTimelineItem[];
    scenes?: Array<{
      scene_name?: unknown;
      sceneName?: unknown;
      text2image?: unknown;
      image2video?: unknown;
      text2ImagePrompt?: unknown;
      image2VideoPrompt?: unknown;
    }>;
  };

  const timelineNodes = timelineNodesFromApiPayload(src);
  if (timelineNodes.length > 0) {
    return timelineNodesToGridRows(timelineNodes);
  }

  if (Array.isArray(src.scenes)) {
    return src.scenes.map((scene, index) => ({
      rowId: newId(),
      sceneId: index + 1,
      sceneName:
        typeof scene.sceneName === 'string'
          ? scene.sceneName
          : typeof scene.scene_name === 'string'
            ? scene.scene_name
            : '',
      text2ImagePrompt: promptForGridDisplay(scene.text2ImagePrompt ?? scene.text2image),
      image2VideoPrompt: promptForGridDisplay(scene.image2VideoPrompt ?? scene.image2video),
    }));
  }

  return [];
}

function sceneNameFromUnknown(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function cellToString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isInteger(value) ? String(Math.trunc(value)) : String(value);
  }
  return String(value).trim();
}

function normalizeImportHeader(value: unknown): string {
  return cellToString(value).toLowerCase();
}

interface ImportPromptColumnMap {
  textToImage: number;
  imageToVideo: number;
}

function mapImportPromptColumns(headerRow: unknown[]): ImportPromptColumnMap | null {
  const map: ImportPromptColumnMap = {
    textToImage: -1,
    imageToVideo: -1,
  };
  headerRow.forEach((cell, index) => {
    const header = normalizeImportHeader(cell);
    if (!header) return;
    if (
      header === 't2i' ||
      header === 'text2image' ||
      header === 'text to image' ||
      (header.includes('text') && header.includes('image') && !header.includes('video'))
    ) {
      map.textToImage = index;
      return;
    }
    if (
      header === 'i2v' ||
      header === 'image2video' ||
      header === 'image to video' ||
      (header.includes('image') && header.includes('video'))
    ) {
      map.imageToVideo = index;
    }
  });
  if (map.textToImage < 0 || map.imageToVideo < 0) return null;
  return map;
}

const IMPORT_PROMPT_COLUMNS_ERROR =
  'Import requires columns: Text to Image Prompt and Image to Video Prompt (or T2I and I2V).';

/**
 * Parses spreadsheet layout: Text to Image Prompt | Image to Video Prompt (or T2I | I2V).
 */
export function parseImportedProjectRowsFromMatrix(matrix: unknown[][]): ProjectGridRow[] {
  if (!matrix.length) {
    throw new Error('Import file is empty.');
  }

  const headerIndex = matrix.findIndex(
    row => Array.isArray(row) && mapImportPromptColumns(row) != null,
  );
  if (headerIndex < 0) {
    throw new Error(IMPORT_PROMPT_COLUMNS_ERROR);
  }
  const columns = mapImportPromptColumns(matrix[headerIndex] ?? []);
  if (!columns) {
    throw new Error(IMPORT_PROMPT_COLUMNS_ERROR);
  }

  const rows: ProjectGridRow[] = [];
  let nextSceneId = 1;
  for (const row of matrix.slice(headerIndex + 1)) {
    if (!Array.isArray(row)) continue;
    const text2ImagePrompt = promptForGridDisplay(workflowPromptFromImportCell(row[columns.textToImage]));
    const image2VideoPrompt = promptForGridDisplay(
      workflowPromptFromImportCell(row[columns.imageToVideo]),
    );
    if (!text2ImagePrompt && !image2VideoPrompt) continue;
    if (!text2ImagePrompt || !image2VideoPrompt) {
      throw new Error(
        'Each row must include both Text to Image and Image to Video prompts, or leave both empty.',
      );
    }
    rows.push({
      rowId: newId(),
      sceneId: nextSceneId++,
      sceneName: '',
      text2ImagePrompt,
      image2VideoPrompt,
    });
  }

  const normalized = normalizeImportedRows(rows);
  if (!normalized.length) {
    throw new Error('No valid scene rows found in import data.');
  }
  return normalized;
}

function sheetMatrixFromWorkbook(workbook: XLSX.WorkBook): unknown[][] {
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('Spreadsheet has no worksheets.');
  }
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) {
    throw new Error('Spreadsheet worksheet is empty.');
  }
  return XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });
}

/**
 * Parses Excel layout: Text to Image Prompt | Image to Video Prompt (or T2I | I2V).
 */
export function parseImportedProjectRowsFromXlsx(buffer: ArrayBuffer): ProjectGridRow[] {
  const workbook = XLSX.read(buffer, { type: 'array' });
  return parseImportedProjectRowsFromMatrix(sheetMatrixFromWorkbook(workbook));
}

/** Parses CSV export (e.g. public Google Sheet) with T2I/I2V or full prompt column headers. */
export function parseImportedProjectRowsFromCsv(csvText: string): ProjectGridRow[] {
  const text = csvText.trim();
  if (!text) {
    throw new Error('Import file is empty.');
  }
  if (text.toLowerCase().startsWith('<!doctype') || text.toLowerCase().includes('<html')) {
    throw new Error(
      'Could not read spreadsheet data. Ensure the Google Sheet is shared publicly (Anyone with the link can view).',
    );
  }
  const workbook = XLSX.read(text, { type: 'string', raw: false });
  return parseImportedProjectRowsFromMatrix(sheetMatrixFromWorkbook(workbook));
}

export interface GoogleSpreadsheetRef {
  spreadsheetId: string;
  gid: string;
}

/** Extracts spreadsheet id and sheet gid from a Google Sheets URL. */
export function parseGoogleSpreadsheetUrl(url: string): GoogleSpreadsheetRef {
  const trimmed = url.trim();
  if (!trimmed) {
    throw new Error('Google Sheet URL is required.');
  }
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new Error('Invalid Google Sheet URL.');
  }
  if (parsed.hostname !== 'docs.google.com') {
    throw new Error('URL must be a Google Sheets link (docs.google.com).');
  }
  const idMatch = parsed.pathname.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  if (!idMatch?.[1]) {
    throw new Error('Could not find spreadsheet ID in URL.');
  }
  const gidFromQuery = parsed.searchParams.get('gid');
  const gidFromHash = parsed.hash.match(/gid=(\d+)/)?.[1];
  const gid = gidFromQuery ?? gidFromHash ?? '0';
  return { spreadsheetId: idMatch[1], gid };
}

export function googleSheetCsvExportUrl(ref: GoogleSpreadsheetRef): string {
  return `https://docs.google.com/spreadsheets/d/${ref.spreadsheetId}/export?format=csv&gid=${ref.gid}`;
}

export function parseImportedProjectRowsFromGoogleSheetCsv(csvText: string): WorkflowGridRow[] {
  return parseImportedProjectRowsFromCsv(csvText);
}

/**
 * Import format supports:
 * - Excel (.xlsx): Text to Image Prompt | Image to Video Prompt (or T2I | I2V)
 * - JSON array of scene objects: [{ text2image, image2video, scene_name? }]
 * - JSON object with `timeline_nodes`/`timelineNodes` or `scenes`
 * - Plain text lines with separators: `text2image | image2video` or `text2image<TAB>image2video`
 */
export async function parseImportedProjectFile(file: File): Promise<ProjectGridRow[]> {
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
    return parseImportedProjectRowsFromXlsx(await file.arrayBuffer());
  }
  if (lowerName.endsWith('.csv')) {
    return parseImportedProjectRowsFromCsv(await file.text());
  }
  return parseImportedProjectRows(await file.text());
}

export function parseImportedProjectRows(rawText: string): ProjectGridRow[] {
  const text = rawText.trim();
  if (!text) {
    throw new Error('Import file is empty.');
  }

  let parsedFromJson: WorkflowGridRow[] = [];
  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      const json = JSON.parse(text) as unknown;
      if (Array.isArray(json)) {
        parsedFromJson = json.map((scene, index) => {
          const row = scene as {
            scene_name?: unknown;
            sceneName?: unknown;
            text2image?: unknown;
            image2video?: unknown;
            text2ImagePrompt?: unknown;
            image2VideoPrompt?: unknown;
          };
          return {
            rowId: newId(),
            sceneId: index + 1,
            sceneName:
              sceneNameFromUnknown(row.sceneName) || sceneNameFromUnknown(row.scene_name),
            text2ImagePrompt: promptForGridDisplay(row.text2ImagePrompt ?? row.text2image),
            image2VideoPrompt: promptForGridDisplay(row.image2VideoPrompt ?? row.image2video),
          };
        });
      } else {
        parsedFromJson = parseRowsFromJsonObject(json);
      }
    } catch {
      throw new Error('Invalid JSON format in import file.');
    }
  }

  if (parsedFromJson.length > 0) {
    const normalized = normalizeImportedRows(parsedFromJson);
    if (!normalized.length) {
      throw new Error('No valid scene rows found in import file.');
    }
    return normalized;
  }

  const lines = text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);
  const rows = lines.map((line, index) => {
    const parts = line.includes('\t')
      ? line.split('\t')
      : line
          .split('|')
          .map(part => part.trim());
    const left = parts[0] ?? '';
    const right = parts.slice(1).join(' | ').trim();
    return {
      rowId: newId(),
      sceneId: index + 1,
      sceneName: '',
      text2ImagePrompt: left.trim(),
      image2VideoPrompt: right,
    };
  });
  const normalized = normalizeImportedRows(rows);
  if (!normalized.length || normalized.some(row => !row.text2ImagePrompt || !row.image2VideoPrompt)) {
    throw new Error(
      'Plain text import requires one scene per line: text2image | image2video (or tab-separated).',
    );
  }
  return normalized;
}

export const workflowTenantIdFromApi = projectTenantIdFromApi;
export const workflowPromptTemplatesFromApi = projectPromptTemplatesFromApi;
export const workflowGenerationSettingsFromApi = projectGenerationSettingsFromApi;
export const workflowSelectorConfigFromApi = projectSelectorConfigFromApi;
export const timelineNodesToProjectGridRows = timelineNodesToGridRows;
export const projectGridRowsToTimelineNodes = gridRowsToTimelineNodes;
export const parseImportedSceneRows = parseImportedProjectFile;
